import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import { Alert, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApiHook from "../../hooks/useApiHook";
import { useCreatePatientModal, usePatientRefresh } from "../../store/modal-context";
import ResponsiveDialog from "../_atoms/ResponsiveDialog";
import cancelButtonSx from "../../styles/cancelButtonSx";

const CreatePatientDialog = () => {
    const navigate = useNavigate();
    const { isOpen, closeModal, payload } = useCreatePatientModal();
    const { refreshPatients } = usePatientRefresh();
    const { response, makeRequest, loading, error } = useApiHook();
    const [patientName, setPatientName] = useState("");
    const [submitRequested, setSubmitRequested] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setPatientName("");
            setSubmitRequested(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !submitRequested || !response || response.state !== 'success') {
            return;
        }

        const createdPatientId = response.data?.id;
        const onSuccess = payload?.onSuccess;
        const shouldNavigate = createdPatientId && payload?.navigateOnSuccess !== false;
        refreshPatients();
        setSubmitRequested(false);
        closeModal();

        if (onSuccess) {
            window.setTimeout(() => {
                onSuccess(response.data);
            }, 0);
        }

        if (shouldNavigate) {
            window.setTimeout(() => {
                navigate(`/patient/${createdPatientId}`);
            }, 0);
        }
    }, [closeModal, isOpen, navigate, payload, refreshPatients, response, submitRequested]);

    const onCloseHandler = () => {
        const onCancel = payload?.onCancel;

        closeModal();

        if (onCancel) {
            window.setTimeout(() => {
                onCancel();
            }, 0);
        }
    };

    const onConfirmHandler = () => {
        const trimmedPatientName = patientName.trim();
        if (!trimmedPatientName) {
            return;
        }

        setSubmitRequested(true);
        makeRequest('/folder', 'post', { name: trimmedPatientName });
    };

    return (
        <ResponsiveDialog
            minWidth="320px"
            minHeight="unset"
            title={(
                <Stack direction="row" alignItems="center" spacing={1}>
                    <FolderSharedOutlinedIcon fontSize="medium" />
                    <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
                        Nuovo Paziente
                    </Typography>
                </Stack>
            )}
            titleFontSize={{ xs: '1.1rem', sm: '1.15rem' }}
            isOpen={isOpen}
            onClose={onCloseHandler}
            actions={(
                <>
                    <Button
                        variant="contained"
                        onClick={onConfirmHandler}
                        disabled={loading || !patientName.trim()}
                        sx={{ minWidth: 88 }}
                    >
                        Crea
                    </Button>
                    <Button variant="outlined" onClick={onCloseHandler} sx={{ ...cancelButtonSx, minWidth: 88 }}>
                        Annulla
                    </Button>
                </>
            )}
        >
            <Stack width="100%" spacing={1.5}>
                <Typography width="100%" textAlign="left" variant="body2" sx={{ opacity: 0.65, lineHeight: 1.35 }}>
                    Inserisci il nome del nuovo paziente.
                </Typography>
                {error && (
                    <Alert severity="error">
                        Impossibile creare il paziente. Riprova.
                    </Alert>
                )}
                <TextField
                    autoFocus
                    fullWidth
                    label="Nome paziente"
                    value={patientName}
                    onChange={(event) => setPatientName(event.target.value)}
                    size="small"
                />
            </Stack>
        </ResponsiveDialog>
    );
};

export default CreatePatientDialog;
